import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  SimpleChanges,
  OnChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  afterNextRender
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { RECAPTCHA_SETTINGS, RecaptchaModule, RecaptchaSettings } from 'ng-recaptcha';
import { CheckboxModule } from 'primeng/checkbox';
import { Observable, map, BehaviorSubject } from 'rxjs';

import { EnvironmentService } from '@environment';
import { ChannelService } from '@usheru-hff/sdk/channel';
import { LocationService } from '@usheru-hff/sdk/location';
import { SubscriptionService } from '@usheru-hff/sdk/subscription';
import { TrackingService } from '@usheru-hff/sdk/tracking';
import { ConsumerLead, getMovieGenres, getMovieTags, Movie, TabType } from '@usheru-hff/shared/domain';
import { LoadingIndicatorComponent } from '@usheru-hff/shared/ui-loading-indicator';
import { EMAIL_PATTERN } from '@usheru-hff/shared/utils';
import {
  TranslationsService,
  TranslationPipe,
  SharedUtilsTranslationModule
} from '@usheru-hff/shared/utils-translation';

/**
 * Component to subscribe one email to different type of notifications.
 *
 * //TODO barbeitoalex propostal - refractor name to something like "notification-subscription".
 * //TODO barbeitoalex propostal - update translate text with an observable to the current language
 * //TODO barbeitoalex propostal - organice life cycle methods. Split in specific method to improve docs.
 */
@Component({
  selector: 'ush-showtime-streaming-subscribe',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    CheckboxModule,
    LoadingIndicatorComponent,
    SharedUtilsTranslationModule,
    RecaptchaModule
    //RecaptchaFormsModule,
  ],
  providers: [
    {
      provide: RECAPTCHA_SETTINGS,
      useValue: {
        siteKey: '6LeF9E0UAAAAAFxfF0-THyi-TB8EjPHBeDyvVNig'
      } as RecaptchaSettings
    }
  ],
  templateUrl: './showtime-streaming-subscribe.component.html',
  styleUrls: ['./showtime-streaming-subscribe.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ShowtimeStreamingSubscribeComponent implements OnInit, OnChanges {
  /**
   * @docsNotRequired
   */
  @Input()
  movie?: Movie;

  /**
   * Type of subscription.
   */
  @Input()
  type: TabType;

  /**
   * @docsNotRequired
   */
  @Input() strTitle: string;

  @Input() customTitleTr: string;
  @Input() customDescTr: string;

  /**
   * Translate value of {@link strTitle}.
   */
  strTitleTranslated: string;

  /**
   * @docsNotRequired
   */
  @Input() strTitleAll: string;

  /**
   * Translate value of {@link strTitleAll}.
   */
  strTitleAllTranslated: string;

  /**
   * @docsNotRequired
   */
  @Input() strDesc: string;

  /**
   * Translate value of {@link strDesc}.
   */
  strDescTranslated: string;

  /**
   * @docsNotRequired
   */
  @Input() strDescAll: string;

  /**
   * Translate value of {@link strDescAll}.
   */
  strDescAllTranslated: string;

  /**
   * @docsNotRequired
   */
  @Input() strThanks: string;

  /**
   * Translate value of {@link strThanks}.
   */
  strThanksTranslated: string;

  /**
   * @docsNotRequired
   */
  @Input() hideOnSuccessSubscription = false;

  /**
   * @docsNotRequired
   */
  @Input() permissionText: string;

  /**
   * @docsNotRequired
   */
  permisionStatus = true;

  /**
   * @docsNotRequired
   *
   * //TODO barbeitoalex propostal - change to boolean.
   */
  @Input() showCaptcha: string;

  /**
   * //TODO barbeitoalex propostal - use form.addControl and deprecated this var.
   */
  captchaStatus = true;

  /**
   * @docsNotRequired
   */
  form: FormGroup;

  /**
   * @deprecard Don't found any use
   */
  email: string;

  token: unknown;

  /**
   * Status to know if some error happen in {@link sendConsumerLead}.
   *
   * //TODO barbeitoalex propostal - refractor name to "errorRequest".
   */
  onError: boolean;

  /**
   * Status to know if the form {@link sendConsumerLead} was lauched but
   * {@link form} is not valid.
   *
   * //TODO barbeitoalex propostal - refractor name to "sendConsumerLeadAttemptNotValid"
   */
  touched = false;

  /**
   * @docsNotRequired
   */
  successSubscription = false;

  /**
   * {@link sendConsumerLead} is in process.
   */
  processing = false;

  /**
   * @docsNotRequired
   * //TODO barbeitoalex propostal - this could be deprecated and use only {@link formSend}.
   */
  sent = false;

  /**
   * @docsNotRequired
   */
  channelName: string;

  userSubscribed = false;

  /**
   * @docsNotRequired
   *
   * //TODO barbeitoalex propostal - refractor name to "formSent"
   */
  @Output()
  formSend = new EventEmitter();

  readonly hideSuccessMessage: boolean;

  constructor(
    private channelService: ChannelService,
    private environmentService: EnvironmentService,
    private formBuilder: FormBuilder,
    private locationService: LocationService,
    private translationService: TranslationsService,
    private subscriptionService: SubscriptionService,
    private cd: ChangeDetectorRef,
    private trackingService: TrackingService
  ) {
    afterNextRender(() => {
      this.insertCapchaScript();
      this.checkSubscriptionStatus();
    });

    this.hideOnSuccessSubscription = this.environmentService.showtimeStreamingSubscribe('hideSuccessMessage');
  }

  /**
   * * Set some initial value
   *
   * * Build {@link form}
   *
   * * Set initial text values to prevent empty values
   *
   * //TODO barbeitoalex propostal - The last step is necesary set it here and not in the var definition?
   */
  ngOnInit(): void {
    if (this.permissionText) this.permisionStatus = false;
    if (this.showCaptcha) this.captchaStatus = false;

    this.form = this.formBuilder.group({
      email: new FormControl('', [Validators.required, Validators.pattern(EMAIL_PATTERN)]),
      captchaStatus: new FormControl(this.captchaStatus, Validators.requiredTrue)
    });

    if (!this.strTitle) this.strTitle = 'public.commons.subscribeForUpdates';
    if (!this.strTitleAll) this.strTitleAll = 'public.commons.notifiedEmail';
    if (!this.strDesc) this.strDesc = 'public.commons.subscribe.email';
    if (!this.strDescAll) this.strDescAll = 'public.commons.subscribeForUpdates.email';
    if (!this.strThanks) this.strThanks = 'public.commons.thanksSubscription';

    // overriting if a custom one exists
    if (this.customTitleTr) {
      this.strTitle = this.customTitleTr;
      this.strTitleAll = this.customTitleTr;
    }
    if (this.customDescTr) {
      this.strDescAll = this.customTitleTr;
      this.strDesc = this.customTitleTr;
    }

    this.setText();
  }

  /**
   *
   * @param changes
   */
  ngOnChanges(changes: SimpleChanges) {
    if (
      !changes.movie?.firstChange &&
      (changes.movie?.currentValue as Movie)?.friendlyUrl != (changes.movie?.previousValue as Movie)?.friendlyUrl
    ) {
      this.setText();
    }
  }

  /**
   * @docsNotRequired
   *
   * //TODO barbeitoalex propostal - refractor name to "setTranslatedTexts"
   */
  setText() {
    this.channelName = this.channelService.channel.name;
    this.strTitleTranslated = this.translationService.translate(this.strTitle).replace('_movie_', this.movie?.title);
    this.strTitleAllTranslated = this.translationService.translate(this.strTitleAll);
    this.strDescTranslated = this.translationService.translate(this.strDesc).replace('_channel_', this.channelName);
    this.strDescAllTranslated = this.translationService
      .translate(this.strDescAll)
      .replace('_channel_', this.channelName);
    this.strThanksTranslated = this.translationService.translate(this.strThanks);
  }

  /**
   * Used to set in a {@link ConsumerLead}.
   */
  get demandedFrom() {
    switch (this.type) {
      case 'in-cinemas':
        return 'Cinemas';
      case 'watch-at-home':
        return 'VOD';
      case 'all':
        return null;
    }
    return null;
  }

  onCheckboxChanged(event) {
    this.permisionStatus = event.checked;
    this.cd.detectChanges();
  }

  private checkSubscriptionStatus() {
    // 2 possible scenarions: checkSubscription of movie demandIt or channel
    if (this.movie) {
      this.checkSubscriptionStatusMovie();
    } else {
      this.checkSubscriptionStatusChannel();
    }
  }

  private checkSubscriptionStatusChannel() {
    const key = 'consumerLead';
    const consumerLead = sessionStorage.getItem(key);
    if (consumerLead) {
      this.userSubscribed = true;
    }
  }
  private checkSubscriptionStatusMovie() {
    const key = `subscribed_${this.movie.friendlyUrl}`;
    const subscribed = localStorage.getItem(key);
    if (subscribed) {
      this.userSubscribed = true;
    }
  }

  /**
   * @docsNotRequired
   *
   * //TODO barbeitoalex propostal - use lastValueFrom
   */
  sendConsumerLead() {
    this.cd.detectChanges();
    this.form.updateValueAndValidity();
    if (this.form.valid && this.permisionStatus) {
      this.processing = true;
      this.subscriptionService.putConsumerLead(this.buildConsumerLead()).subscribe(
        () => {
          this.processing = false;
          this.sent = true;
          this.cd.detectChanges();
          this.trackEvent();
          this.saveConsumerLeadOnSessionStorage();
          this.formSend.next(true);
          if (this.hideOnSuccessSubscription) {
            setTimeout(() => {
              this.onSubscriptionComplete();
            }, 5000);
          } else {
            this.cd.detectChanges();
          }
        },
        err => {
          this.processing = false;
          this.onError = true;
          this.cd.detectChanges();
          console.error(err);
        }
      );
    } else {
      this.touched = true;
      this.cd.detectChanges();
    }
  }

  private saveConsumerLeadOnSessionStorage() {
    if (this.movie) {
      localStorage.setItem(`subscribed_${this.movie.friendlyUrl}`, 'true');
    } else {
      sessionStorage.setItem('consumerLead', 'true');
    }
  }

  private onSubscriptionComplete() {
    this.successSubscription = true;
    this.cd.detectChanges();
  }

  trackEvent() {
    this.trackingService.trackEvent({
      action: 'Lead',
      properties: {
        label: this.movie ? this.movie.title : this.channelService.channel.name,
        category: this.movie ? 'MovieSubscription' : 'BrandSubscription',
        movie: this.movie?.title,
        idMovie: this.movie?.id,
        idChannel: this.channelService.channel.id,
        channel: this.channelService.channel.name,
        imdb: this.movie?.imdb,
        tagsMovie: getMovieTags(this.movie),
        genresMovie: getMovieGenres(this.movie)
      }
    });
  }

  /**
   * @docsNotRequired
   * @returns
   */
  private buildConsumerLead(): ConsumerLead {
    const consumerLead: ConsumerLead = {
      emailAddress: this.form.get('email').value,
      language: this.translationService.currentLanguage().id.toString()
    };

    this.type != 'all' && (consumerLead.type = 'demandIt');
    this.locationService.selectedCountry && (consumerLead.country = this.locationService.selectedCountry.id);
    this.demandedFrom && (consumerLead.demandedFrom = this.demandedFrom);
    this.locationService.userLocation && (consumerLead.longitude = this.locationService.userLocation.longitude);
    this.locationService.userLocation && (consumerLead.latitude = this.locationService.userLocation.latitude);
    this.movie && (consumerLead.movie = this.movie.id);
    return consumerLead;
  }

  /**
   * @docsNotRequired
   */
  private insertCapchaScript() {
    if (this.showCaptcha) {
      const foundScript = document.querySelector('#capcha-script');
      if (!foundScript) {
        const script = document.createElement('script');
        script.id = 'capcha-script';
        script.src = 'https://www.google.com/recaptcha/api.js?render=explicit&onload=initRecaptcha';
        script.async = true;
        script.defer = true;
        document.body.appendChild(script);
      }
    }
  }

  /**
   * Event trigger when the capcha is resolved to update {@link form}.
   * @param event
   */
  onCapchaResponse() {
    this.captchaStatus = true;
    this.form.controls.captchaStatus.setValue(true);
    this.cd.detectChanges();
  }

  /**
   * Used to restore some special character from a encrypted text.
   * @param str
   * @returns
   */
  getDecodeText(str: string) {
    if (!this.isEncoded(str)) return str;
    return decodeURIComponent(str);
  }

  /**
   * Used in {@link getDecodeText}.
   * @param str
   * @returns
   */
  private isEncoded(str: string) {
    return typeof str == 'string' && decodeURIComponent(str) !== str;
  }
}
